/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "./Room.css";
import { useSocket } from "../context/SocketContext";
import { VideoContainer, VideoControls } from "../components/VideoControls";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { roomLeave } from "../store/roomSlice";
import { v4 as UUIDv4 } from "uuid";
import Peer from "peerjs";
import { decryptData, encryptData } from "../helpers/cryptoSocketData";
import { getBrowser } from "../helpers/browsers";

const Room = () => {
  const { roomId } = useParams();
  const { loadingLeave, error } = useSelector((state) => state?.room);
  const username = JSON.parse(sessionStorage.getItem("username"))
  const userId = JSON.parse(sessionStorage.getItem("userId"))
  const userProfileImg = JSON.parse(sessionStorage.getItem("profileImg"))
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isDisable, setIsDisable] = useState(false);
  const [cameras, setCameras] = useState();
  const [facingMode, setFacingMode] = useState("user");
  const [sdpSemantics, setSdpSemantics] = useState("unified-plan");
  const socket = useSocket();
  const dispatch = useDispatch();
  const [roomUsers, setRoomUsers] = useState({});
  const [user, setUser] = useState(null);
  const [peerStreams, setPeerStreams] = useState({});
  const [stream, setStream] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [shouldMediaInitialized, setShouldMediaInitialized] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const peerId = JSON.parse(sessionStorage.getItem("peerId")) ?? UUIDv4();
  const location = useLocation();
  const nameFromState = location?.state?.name;
  const name = nameFromState || JSON.parse(sessionStorage.getItem("roomName"));
  const currentPath = sessionStorage.getItem("currUrlPath") || location.pathname;
  const navigate = useNavigate();

  // const [leaveTimer, setLeaveTimer] = useState(null);

  useEffect(() => {
    if (socket) {
      if (user && isInitialized) {
        // console.log("Joining room with peer ID:", user.id);
        sessionStorage.setItem("peerId", JSON.stringify(user.id));
        sessionStorage.setItem("isMicEnable", JSON.stringify(isAudioEnabled));
        sessionStorage.setItem("isVidEnable", JSON.stringify(isVideoEnabled));
        socket.emit(
          "joined-room",
          encryptData(
            JSON.stringify({
              roomId,
              peerId: user.id,
              username: username,
              userId: userId,
              profileImage: userProfileImg,
              isVideoEnabled: isVideoEnabled,
              isAudioEnabled: isAudioEnabled,
            })
          )
        );
        socket.emit(
          "store-peer-id",
          encryptData(JSON.stringify({ peerId: user.id }))
        );
        socket.emit(
          "request-room-users",
          encryptData(JSON.stringify({ roomId }))
        );
      }
    }
  }, [user, socket, roomId, isInitialized, username, userId]);

  const enumerateDeviceCheck = async () => {
    try {
      if (!navigator?.mediaDevices || !navigator?.mediaDevices?.enumerateDevices) {
        console.warn(
          "Failed to switch camera. Device might not support multiple cameras."
        );
        return false;
      }

      const permissionStatus = await navigator.permissions.query({ name: "camera" });
      if (permissionStatus.state !== "granted") {
        const tempStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: true,
        });
        tempStream.getTracks().forEach(track => track.stop());
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === "videoinput");

      // console.log("videoDevices", videoDevices[0].deviceId);

      // alert(`videoDevices ${videoDevices}`)
      if (videoDevices.length > 0) {
        setCameras(videoDevices);
        return true;
      } else {
        console.warn("No video devices found.");
        return false;
      }
    } catch (error) {
      console.error("Error checking devices:", error);
      return false;
    }
  };

  // Get supported constraints based on browser
  const getVideoConstraints = useCallback(() => {
    const browser = getBrowser();
    const isAndroid = /Android/i.test(navigator.userAgent);
    // const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setFacingMode(facingMode === "environment" ? "user" : "environment");

    const baseConstraints = {
      width: { min: 320, ideal: 640 },
      height: { min: 240, ideal: 480 },
      frameRate: { min: 15, ideal: 30 },
      facingMode: facingMode,
    };

    if (isAndroid) {
      return {
        width: { ideal: 640 },
        height: { ideal: 480 },
        frameRate: { ideal: 24 },
        facingMode: { exact: facingMode },
      };
    }
    if (isIOS) {
      return {
        ...baseConstraints,
        facingMode: { exact: facingMode },
      };
    }

    if (browser === "safari") {
      const safariVersion = parseInt(navigator.userAgent.match(/Version\/(\d+)/)?.[1] || "15", 10);
      if (safariVersion < 12) setSdpSemantics("plan-b");
      return baseConstraints;
    }

    if (browser === "firefox") {
      return { ...baseConstraints, mozMediaSource: "camera" };
    }
    // Chrome, Edge, and others
    return baseConstraints;
  }, [facingMode]);



  const stopMediaTracks = useCallback(() => {
    if (stream) {
      // console.log("Stopping Tracks..");
      stream.getTracks().forEach((track) => {
        track.stop();
      });
      setStream(null);
      // console.log("Tracks Stopped");
    }
  }, [stream]);

  const fetchUserMedia = async () => {
    try {
      stopMediaTracks();
      // Check if getUserMedia is available
      if (!navigator.mediaDevices?.getUserMedia) {
        // Legacy support for older browsers
        const getUserMedia =
          navigator.getUserMedia ||
          navigator.webkitGetUserMedia ||
          navigator.mozGetUserMedia ||
          navigator.msGetUserMedia;

        if (!getUserMedia) {
          throw new Error("getUserMedia not supported in this browser");
        }
      }

      const constraints = {
        video: getVideoConstraints(),
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      // iOS-specific stream handling
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS) {
        // Ensure audio track is properly initialized
        const audioTrack = stream.getAudioTracks()[0];
        if (audioTrack) {
          audioTrack.enabled = true;
        }

        // Ensure video track is properly initialized
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack) {
          videoTrack.enabled = true;
        }
      }

      setStream(stream);
      return stream;
    } catch (error) {
      console.error("Error fetching user media stream:", error);

      // Handle specific browser errors
      if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
        if (error.name === "NotAllowedError") {
          toast.error(
            "Please enable camera and microphone access in your iOS settings"
          );
        } else if (error.name === "NotReadableError") {
          toast.error("Please close other apps that might be using the camera");
        }
      } else {
        // Original error handling for other devices
        if (
          error.name === "NotAllowedError" ||
          error.name === "PermissionDeniedError"
        ) {
          toast.error("Camera and microphone permissions are required");
        } else if (
          error.name === "NotFoundError" ||
          error.name === "DevicesNotFoundError"
        ) {
          toast.error("No camera or microphone found");
        } else if (
          error.name === "NotReadableError" ||
          error.name === "TrackStartError"
        ) {
          toast.error("The camera or microphone is already in use")
        } else {
          toast.error("Failed to access media devices");
        }
      }

      return null;
    }
  };

  const switchCamera = async () => {
    try {
      setIsDisable(true);
      // Re-enumerate devices to get the latest list
      const hasDevices = await enumerateDeviceCheck();
      if (!hasDevices || cameras.length < 2) {
        toast.error("Only one camera available, can't switch.");
        return;
      }
      // Stop current tracks
      if (stream) {
        stream.getVideoTracks().forEach(track => track.stop());
      }
      // Get new stream with updated facing mode
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: getVideoConstraints(),
        audio: true
      });

      if (newStream) {
        newStream.getAudioTracks().forEach(track => {
          track.enabled = JSON.parse(sessionStorage.getItem("isMicEnable"));
        })
        setStream(newStream);
        await updatePeerConnections(newStream);
      }

    } catch (error) {
      console.error("Error switching camera:", error);
      toast.error(`Error switching camera ${error}`);
    } finally {
      setIsDisable(false);
    }
  };

  const updatePeerConnections = async (newStream) => {
    if (!user) return;

    const videoTrack = newStream.getVideoTracks()[0];
    const audioTrack = newStream.getAudioTracks()[0];

    // Get all peer connections
    Object.values(user.connections).forEach((connections) => {
      connections.forEach((connection) => {
        const peerConnection = connection.peerConnection;

        // Get all senders for this peer connection
        const senders = peerConnection.getSenders();

        // Replace both video and audio tracks
        senders.forEach((sender) => {
          if (sender.track.kind === "video" && videoTrack) {
            sender.replaceTrack(videoTrack).catch((error) => {
              console.error("Error replacing video track:", error);
            });
          }
          if (sender.track.kind === "audio" && audioTrack) {
            sender.replaceTrack(audioTrack).catch((error) => {
              console.error("Error replacing audio track:", error);
            });
          }
        });

        // Negotiate connection if needed
        try {
          peerConnection.negotiationneeded = () => {
            peerConnection
              .createOffer()
              .then((offer) => peerConnection.setLocalDescription(offer))
              .then(() => {
                // Send the new offer to the peer
                socket.emit("renegotiate", {
                  to: connection.peer,
                  description: peerConnection.localDescription,
                });
              })
              .catch((error) => {
                console.error("Error during renegotiation:", error);
              });
          };
        } catch (error) {
          console.error("Error setting up renegotiation:", error);
        }
      });
    });
  };

  // Initialize peer connection
  useEffect(() => {
    if (!shouldMediaInitialized) return;
    const initializePeer = async () => {
      await enumerateDeviceCheck();
      const mediaStream = await fetchUserMedia();
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      const newPeer = new Peer(peerId, {
        config: {
          iceServers: [
            { url: "stun:stun.l.google.com:19302" },
            { url: "stun:stun1.l.google.com:19302" },
            { url: "stun:stun2.l.google.com:19302" },
            { url: "stun:stun3.l.google.com:19302" },
            { url: "stun:stun4.l.google.com:19302" },
            {
              urls: "turn:relay.metered.ca:443",
              username: "user",
              credential: "pass",
            },
          ],
          iceTransportPolicy: "all",
          sdpSemantics: sdpSemantics,

          ...(isIOS && {
            bundlePolicy: "max-bundle",
            rtcpMuxPolicy: "require",
            // Enable hardware acceleration when available
            enableDtlsSrtp: true,
          }),
        },
        debug: 3,
      });

      if (isIOS) {
        newPeer.on("connection", (conn) => {
          conn.on("error", (err) => {
            console.error("iOS peer connection error:", err);
            // Attempt recovery
            setTimeout(() => {
              if (mediaStream) {
                setupCallEventHandlers(conn, conn.peer);
              }
            }, 1000);
          });
        });
      }

      newPeer.on("open", () => {
        setUser(newPeer);
        setIsInitialized(true);
        sessionStorage.setItem("currUrlPath", currentPath);
        // console.log("Peer connection initialized with ID:", newPeer.id);
      });

      newPeer.on("error", (err) => {
        console.error("PeerJS Error:", err);
        // if (err.type === "webrtc") {
        //   toast.error("Safari WebRTC error, please check permissions.");
        // }
      });

      newPeer.on("disconnected", async () => {
        // console.log("Peer disconnected. Attempting to reconnect...");
        setIsReconnecting(true);
        try {
          newPeer.reconnect();
        } catch (error) {
          console.error("Peer reconnection failed:", error);
          // Attempt to create new peer connection
          setTimeout(initializePeer, 2000);
        }
      });

      // Handle incoming calls immediately after peer creation
      newPeer.on("call", (call) => {
        // console.log("Receiving call from:", call.peer);
        if (mediaStream) {
          call.answer(mediaStream);
          setupCallEventHandlers(call, call.peer);
        }
      });
    };

    initializePeer();

    return () => {
      // Cleanup
      stopMediaTracks();
      if (user) {
        user.destroy();
      }
    };
  }, [shouldMediaInitialized]);

  useEffect(() => {
    if (!user || !stream || !isInitialized) return;

    const handleUserJoined = (data) => {
      const decryptUserJoinedData = decryptData(data);
      if (decryptUserJoinedData.peerId === user.id) return;
      setRoomUsers(decryptUserJoinedData.userNamemap);
      const call = user.call(decryptUserJoinedData.peerId, stream);
      if (call) {
        setupCallEventHandlers(call, decryptUserJoinedData.peerId);
      } else {
        console.error(
          "Failed to initiate call to:",
          decryptUserJoinedData.peerId
        );
      }
    };
    const handleRoomUsers = (data) => {
      const decryptRoomUsersData = decryptData(data);
      const filteredUsers = Object.fromEntries(
        Object.entries(decryptRoomUsersData.userNamemap)?.filter(
          ([peerId, user]) => user.roomId === roomId
        )
      );
      setRoomUsers(filteredUsers);
    };

    // Handle user left event
    const handleUserLeft = (data) => {
      const UserLeftData = decryptData(data);
      setRoomUsers(UserLeftData.userNamemap);
      setPeerStreams((prevStreams) => {
        const updatedStreams = { ...prevStreams };
        delete updatedStreams[UserLeftData.peerId];
        return updatedStreams;
      });
    };

    const handleDisconnect = (disconnectedPeerId) => {
      console.log("disconnectedPeerId", disconnectedPeerId);
      console.log("User disconnected:", disconnectedPeerId);
      setPeerStreams((prevStreams) => {
        const updatedStreams = { ...prevStreams };
        delete updatedStreams[disconnectedPeerId];
        return updatedStreams;
      });
      // console.log("peerStream", peerStreams)
    };
    const handleMediaStateChanged = (data) => {
      const decryptRoomUsersData = JSON.parse(decryptData(data));
      const filteredUsers = Object.fromEntries(
        Object.entries(decryptRoomUsersData.userNamemap).filter(
          ([peerId, user]) => user?.roomId === roomId
        )
      );
      setRoomUsers(filteredUsers);
    };

    if (socket) {
      socket.on("user-joined", handleUserJoined);
      socket.on("room-users", handleRoomUsers);
      socket.on("media-state-changed", handleMediaStateChanged);
      socket.on("user-left", handleUserLeft);
      socket.on("disconnect", handleDisconnect);

      return () => {
        socket.off("user-joined", handleUserJoined);
        socket.off("room-users", handleRoomUsers);
        socket.off("media-state-changed", handleMediaStateChanged);
        socket.off("disconnect", handleDisconnect);
        socket.off("user-left", handleUserLeft);
      };
    }
  }, [user, stream, isInitialized]);

  const handleReconnection = useCallback(async () => {
    if (!roomId || !user || !socket) return;
    // console.log("Attempting to reconnect to room:", roomId);
    socket.emit(
      "joined-room",
      encryptData(
        JSON.stringify({
          roomId,
          peerId: user.id,
          username: username,
          userId: userId,
          profileImage: userProfileImg,
          isVideoEnabled: JSON.parse(sessionStorage.getItem("isVidEnable")),
          isAudioEnabled: JSON.parse(sessionStorage.getItem("isMicEnable"))
        })
      )
    );

    socket.emit(
      "request-room-users",
      encryptData(JSON.stringify({ roomId }))
    );
  }, [roomId, user, socket]);

  useEffect(() => {
    if (!socket) return;

    const onReconnectAttempt = (attempt) => {
      console.log(`Reconnection attempt ${attempt}`);
      setIsReconnecting(true);
      toast.dismiss("reconnecting");
      toast.info(
        `Connection lost. Reconnecting...`,
        { toastId: "reconnecting" }
      );
    };

    const onReconnect = () => {
      console.log("Socket reconnected successfully");
      setIsReconnecting(false);
      handleReconnection();
      toast.dismiss("reconnecting");
    };

    const onReconnectFailed = () => {
      // console.log("Socket reconnection failed");
      setIsReconnecting(false);
      navigate('/lobby')
      toast.dismiss("reconnecting");
    };


    socket.io.on("reconnect_attempt", onReconnectAttempt);
    socket.io.on("reconnect", onReconnect);
    socket.io.on("reconnect_failed", onReconnectFailed);

    return () => {
      socket.io.off("reconnect_attempt", onReconnectAttempt);
      socket.io.off("reconnect", onReconnect);
      socket.io.off("reconnect_failed", onReconnectFailed);
    };
  }, [socket, handleReconnection, isInitialized]);

  const setupCallEventHandlers = useCallback((call, remotePeerId) => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    call.on("stream", async (remoteStream) => {

      // console.log("Received remote stream from:", remotePeerId);
      if (isIOS) {
        // Ensure stream is properly initialized on iOS
        remoteStream.getAudioTracks().forEach((track) => {
          track.enabled = true;
        });
        remoteStream.getVideoTracks().forEach((track) => {
          track.enabled = true;
        });
      }

      const senders = call.peerConnection.getSenders();
      // Process each sender asynchronously
      await Promise.all(
        senders.map(async (sender) => {
          try {
            if (sender.track) {
              const params = await sender.getParameters();
              if (!params.encodings) {
                params.encodings = [{}];
              }

              // Lower bitrates for iOS to prevent performance issues
              if (isIOS) {
                if (sender.track.kind === "video") {
                  params.encodings[0].maxBitrate = 500 * 1000;
                }
                if (sender.track.kind === "audio") {
                  params.encodings[0].maxBitrate = 150 * 1000;
                }
              } else {
                if (sender.track.kind === "video") {
                  params.encodings[0].maxBitrate = 500 * 1000;
                }
                if (sender.track.kind === "audio") {
                  params.encodings[0].maxBitrate = 150 * 1000;
                }
              }

              await sender.setParameters(params);
            }
          } catch (error) {
            console.warn(
              `Failed to set parameters for ${sender.track?.kind} track:`,
              error
            );
          }
        })
      );
      setPeerStreams((prevStreams) => {
        if (!prevStreams[remotePeerId]) {
          // console.log("Adding new stream for:", remotePeerId);
          return {
            ...prevStreams,
            [remotePeerId]: remoteStream,
          };
        }
        return prevStreams;
      });
    });

    call.on("close", () => {
      // console.log("Call closed with:", remotePeerId);
      setPeerStreams((prevStreams) => {
        const updatedStreams = { ...prevStreams };
        delete updatedStreams[remotePeerId];
        return updatedStreams;
      });
    });

    call.on("error", (error) => {
      console.error("Call error with peer:", remotePeerId, error);
      setPeerStreams((prevStreams) => {
        const updatedStreams = { ...prevStreams };
        delete updatedStreams[remotePeerId];
        return updatedStreams;
      });
    });
  }, []);

  // when a user leaves in-directly....
  useEffect(() => {
    const previousRoom = sessionStorage.getItem("prevRoom");
    if (previousRoom === currentPath) {
      // Prevent access and redirect to lobby
      stopMediaTracks();
      toast.info("Please click on join button.");
      navigate("/lobby", { replace: true });
    } else {
      setShouldMediaInitialized(true);
    }

    let shouldPreventBackNavigation = false;
    let userActiveStatus = null;
    const handleLeaveRoom = () => {
      if (document.visibilityState === "visible") {
        if (userActiveStatus) {
          clearTimeout(userActiveStatus);
          userActiveStatus = null;
        }
      }
      if (document.visibilityState === "hidden") {
        const userInactive = setTimeout(() => {
          leaveRoom();
        }, 300000);
        userActiveStatus = userInactive;
      }
    };
    const handlePopState = (e) => {
      e.preventDefault();
      if (shouldPreventBackNavigation) {
        shouldPreventBackNavigation = false;
        return;
      }
      const confirmLeave = window.confirm(
        "Are you sure you want to leave the room?"
      );
      if (confirmLeave) {
        leaveRoom()
      } else {
        shouldPreventBackNavigation = true;
        window.history.go(1);
      }
    };

    if (location.pathname !== currentPath) {
      alert(
        "You cannot change Path directly.\nYou must need to leave the room."
      );
      navigate(currentPath, { replace: true });
    }
    window.history.pushState(null, "", window.location.href);

    window.addEventListener("popstate", handlePopState);
    document.addEventListener("visibilitychange", handleLeaveRoom);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      document.removeEventListener("visibilitychange", handleLeaveRoom);
    };
  }, [user, location.pathname, navigate, currentPath]);

  const toggleAudio = () => {
    try {
      setIsDisable(true)
      if (stream) {
        stream.getAudioTracks().forEach((track) => {
          track.enabled = !isAudioEnabled;
        });
        sessionStorage.setItem("isMicEnable", JSON.stringify(!isAudioEnabled))
        setIsAudioEnabled(!isAudioEnabled);

        // Notify remote users about the video state change
        if (socket && socket.connected) {
          socket.emit(
            "media-state-changed",
            encryptData(
              JSON.stringify({
                peerId: user.id,
                isAudioEnabled: !isAudioEnabled,
                isVideoEnabled: isVideoEnabled,
              })
            )
          );
        }
      }
    } catch (error) {
      setIsDisable(false)
    } finally {
      setIsDisable(false)
    }
  };

  const toggleVideo = () => {
    try {
      setIsDisable(true)
      if (stream) {
        stream.getVideoTracks().forEach((track) => {
          track.enabled = !isVideoEnabled;
        });
        sessionStorage.setItem("isVidEnable", JSON.stringify(!isVideoEnabled))
        setIsVideoEnabled(!isVideoEnabled);

        // Notify remote users about the video state change
        if (socket && socket.connected) {
          socket.emit(
            "media-state-changed",
            encryptData(
              JSON.stringify({
                peerId: user.id,
                isVideoEnabled: !isVideoEnabled,
                isAudioEnabled: isAudioEnabled,
              })
            )
          );
        }
      }
    }
    catch (error) {
      setIsDisable(false)
    } finally {
      setIsDisable(false)
    }
    // when user disables video, user leave the room after 10 seconds
    // if (leaveTimer) {
    //   clearTimeout(leaveTimer);
    //   setLeaveTimer(null);
    // }

    // if (isVideoEnabled) {
    //   toast.info(
    //     "You will leave room in 10 seconds due to inactive camera..."
    //   );
    //   const timer = setTimeout(() => {
    //     leaveRoom();
    //   }, 10000);
    //   setLeaveTimer(timer);
    // }
  }


  const leaveRoom = async () => {
    // console.log("Leaving room");
    stopMediaTracks();
    if (user && socket) {
      socket.emit(
        "leave-room",
        encryptData(
          JSON.stringify({
            roomId,
            peerId: user.id,
            username: username,
            userId: userId,
          })
        )
      );
      try {
        if (user.connections) {
          Object.values(user.connections).forEach((connections) => {
            connections.forEach((conn) => {
              if (conn.peerConnection) {
                // Stop all remote tracks
                const senders = conn.peerConnection.getSenders();
                senders.forEach((sender) => {
                  if (sender.track) {
                    sender.track.stop();
                  }
                });
                // Close the peer connection
                conn.peerConnection.close();
              }
            });
          });
        }
        const response = await dispatch(roomLeave({ roomId })).unwrap();
        if (response?.success) {
          sessionStorage.removeItem("currUrlPath");
          sessionStorage.removeItem("peerId");
          sessionStorage.setItem("prevRoom", location.pathname);
          navigate("/lobby", { replace: true });
        }
      } catch (error) {
        console.error(error);
        toast.error(error.message);
      }
    }
  };

  const totalParticipants = 1 + Object.keys(peerStreams).length;
  if (!isInitialized) {
    return (
      <div className="main-box">
        <div className="waiting-loader">
          <h2 style={{ padding: "10px 35px" }}>Tuning in, getting you connected...</h2>
          <div className="loader"></div>
        </div>
      </div>
    )
  }

  if (loadingLeave) {
    stopMediaTracks();
    return (
      <div className="main-box">
        <div className="waiting-loader">
          <h2 style={{ padding: "10px 35px" }}>Leaving the room</h2>
          <div className="loader"></div>
        </div>
      </div>
    );
  }
  if (error) {
    navigate("/lobby", { replace: true })
  }

  if (stream) {
    return (
      <>
        <div className="room-container">
          <div className="room-content">
            {/* Reconnection Banner */}
            {isReconnecting && (
              <div className="reconnecting-banner">
                <p className="reconnecting-text">
                  Connection lost. Attempting to reconnect...
                </p>
              </div>
            )}

            {/* Video Grid */}
            <div className={`video-grid participants-${totalParticipants}`}>
              {/* Local Stream */}
              {stream && (
                <VideoContainer
                  flip={facingMode === "environment" ? false : true}
                  stream={stream}
                  username={username}
                  profileImg={userProfileImg}
                  isLocal={true}
                  isVideoEnabled={isVideoEnabled}
                  isAudioEnabled={isAudioEnabled}
                  isReconnecting={isReconnecting}
                />
              )}

              {/* Remote Streams */}
              {Object.entries(peerStreams).map(([peerId, remoteStream]) => (
                <VideoContainer
                  key={peerId}
                  stream={remoteStream}
                  isVideoEnabled={roomUsers[peerId]?.isVideoEnabled ?? true}
                  isAudioEnabled={roomUsers[peerId]?.isAudioEnabled ?? true}
                  username={String(roomUsers[peerId]?.username)}
                  profileImg={roomUsers[peerId]?.profileImage}
                  isLocal={false}
                />
              ))}
            </div>

            {/* Controls Bar */}
            <div className="controls-container">
              <div className="controls-content">
                <VideoControls
                  roomName={name}
                  roomId={roomId}
                  isAudioEnabled={isAudioEnabled}
                  isVideoEnabled={isVideoEnabled}
                  onToggleAudio={toggleAudio}
                  onToggleVideo={toggleVideo}
                  isDisable={isDisable}
                  switchCamera={switchCamera}
                  onLeaveRoom={leaveRoom}
                  users={roomUsers}
                />
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
};

export default Room;
